import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from "react-router-dom";
import ViewHeader from "../viewHeader/viewHeader";
import apiService from "../../utils/api";
import MissingOrdersModal from "./missingOrdersModal";

export default function FoodOrderStats() {
  // State
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [schools, setSchools] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMissingOrders, setSelectedMissingOrders] = useState(null);

  // Hooks
  const navigate = useNavigate();

  // Effects
  useEffect(() => {
    const authToken = sessionStorage.getItem("authToken");
    const associatedSchools = sessionStorage.getItem("associatedSchools");

    if (authToken) {
      apiService.getSchool(authToken, undefined, associatedSchools).then((response) => {
        setSchools(response.data);
      }).catch((error) => {
        console.error("Error fetching schools:", error);
      });
    } else {
      navigate("/");
    }
  }, [navigate]);

  // Handlers
  const handleGetStats = async () => {
    if (!fromDate || !toDate) {
      setError("Please select from date and to date");
      return;
    }

    setIsLoading(true);
    setError("");
    
    try {
      const authToken = sessionStorage.getItem("authToken");
      const formattedFromDate = fromDate.format('YYYY-MM-DD');
      const formattedToDate = toDate.format('YYYY-MM-DD');
      
      const response = await apiService.getFoodOrderStats(
        authToken,
        formattedFromDate,
        formattedToDate,
        selectedSchool || null
      );
      
      setOrderStats(response.data);
    } catch (error) {
      console.error("Error fetching food order stats:", error);
      setError("Failed to fetch food order statistics. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMissingOrdersClick = (schoolData) => {
    // Pass the raw day-by-day data to show proper hierarchy
    setSelectedMissingOrders({
      school_name: schoolData.school_name,
      days: schoolData.days
    });
    setModalOpen(true);
  };

  // Helper function to aggregate food items across all days
  const aggregateFoodItems = (schoolData) => {
    const aggregated = {};
    
    Object.values(schoolData.days).forEach(day => {
      day.food_items.forEach(item => {
        if (aggregated[item.food_item_id]) {
          aggregated[item.food_item_id].order_count += item.order_count;
        } else {
          aggregated[item.food_item_id] = {
            ...item
          };
        }
      });
    });
    
    return Object.values(aggregated);
  };

  // Helper function to get total missing orders across all days
  const getTotalMissingOrders = (schoolData) => {
    return Object.values(schoolData.days).reduce((total, day) => {
      return total + day.missing_orders.count;
    }, 0);
  };

  // Helper function to get total orders across all days
  const getTotalOrders = (schoolData) => {
    return aggregateFoodItems(schoolData).reduce((total, item) => {
      return total + item.order_count;
    }, 0);
  };

  return (
    <Container>
      <ViewHeader screenName="Food Order Statistics" />
      
      {/* Filters */}
      <Box display="flex" flexDirection="row" gap={2} mb={3} mt={2} justifyContent="center">
        <DatePicker
          label="From Date"
          value={fromDate}
          onChange={setFromDate}
          sx={{ minWidth: 200 }}
        />
        <DatePicker
          label="To Date"
          value={toDate}
          onChange={setToDate}
          sx={{ minWidth: 200 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>School (Optional)</InputLabel>
          <Select
            value={selectedSchool}
            onChange={(e) => setSelectedSchool(e.target.value)}
            label="School (Optional)"
          >
            <MenuItem value="">
              <em>All Schools</em>
            </MenuItem>
            {schools.map((school) => (
              <MenuItem key={school.schoolId} value={school.schoolId}>
                {school.schoolName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button 
          variant="contained" 
          onClick={handleGetStats}
          disabled={isLoading}
        >
          Get Statistics
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Results */}
      {orderStats && !isLoading && (
        <Box>
          {Object.entries(orderStats).map(([schoolId, schoolData]) => (
            <Card key={schoolId} sx={{ mb: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                {/* School Header */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                    {schoolData.school_name}
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Chip 
                      label={`${getTotalOrders(schoolData)} Total Orders`}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                    <Chip 
                      label={`${getTotalMissingOrders(schoolData)} Missing Orders`}
                      color="warning"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Box>
                
                {/* Food Items */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.secondary' }}>
                    Food Items Summary
                  </Typography>
                  <Grid container spacing={2}>
                    {aggregateFoodItems(schoolData).map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.food_item_id}>
                        <Box 
                          sx={{ 
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#fafafa',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                              borderColor: '#d0d0d0'
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {item.food_item_name}
                          </Typography>
                          <Typography variant="h6" color="primary">
                            {item.order_count}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            orders
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Missing Orders Action */}
                {getTotalMissingOrders(schoolData) > 0 && (
                  <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => handleMissingOrdersClick(schoolData)}
                      sx={{ borderRadius: 1.5 }}
                    >
                      View {getTotalMissingOrders(schoolData)} Missing Orders
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Missing Orders Modal */}
      <MissingOrdersModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        missingOrdersData={selectedMissingOrders}
      />
    </Container>
  );
} 